import { useActorMember, useAuthContext, useSubjectMember, useSubjectUpdate } from '@healthblocks-io/core'
import { differenceInYears } from 'date-fns'
import { useEffect, useRef } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'

import { NativePlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { PushNotificationsProvider } from '@/capacitor/components/push-notifications'
import { mixpanel } from '@/mixpanel'
import { useAuthentication } from '@/modules/auth/hooks/useAuthentication'
import { useBiometrics } from '@/modules/biometrics/hooks/useBiometrics'
import { useBiometricsPermission } from '@/modules/biometrics/hooks/useBiometricsPermission'
import { BiometricsAppPermission } from '@/modules/biometrics/state/biometrics.atom'
import { IdleTimer } from '@/modules/idle-timer/components/IdleTimer'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { getAgeSegment } from '@/modules/shared/helpers/age-segment'
import { useLockoutTime } from '@/modules/shared/hooks/useLockoutTime'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'

const Authenticated = ({ children }: { children: React.ReactNode }) => {
  // Refs
  // ------------------------------------------------------------------------- /
  const previousAuthState = useRef<boolean | null | undefined>(undefined)

  // Hooks
  // ------------------------------------------------------------------------- /
  const auth = useAuthContext()
  const { setLockout } = useLockoutTime()
  const location = useLocation()
  const { tenant } = useParams<{ tenant: string }>()
  const actorMember = useActorMember()
  const subjectMember = useSubjectMember()
  const { biometricPermission } = useBiometricsPermission()
  const { isAvailable: biometricsAreAvailable } = useBiometrics()
  const { isAuthenticated, isLocked, shouldBeLoading: authLoading } = useAuthentication()
  const loggedInForMandatee = useLoggedInForMandatee()

  const [{ mutate, isLoading: updateSubjectLoading }, updateSubject] = useSubjectUpdate()

  // Template variables
  // ------------------------------------------------------------------------- /

  // auth.data.current can be:
  // - undefined: bootstrapping (initial loading)
  // - null: logged out
  // - AuthState: logged in
  const isAuthBootstrapping = auth.data.current === undefined

  const isLoading = !actorMember || !subjectMember || updateSubjectLoading || authLoading

  // Get the tenant scoped route
  const pathSegments = location.pathname.split('/')
  const tenantScopedRoute = pathSegments[2] ?? ''

  // Memoize primitive values to avoid unnecessary effect runs
  const isOnboardingRoute = tenantScopedRoute.startsWith('onboarding')
  const isBiometricRoute = tenantScopedRoute.startsWith('biometric')

  const actorId = actorMember?.id
  const actorBirthDate = actorMember?.birthDate
  const actorGender = actorMember?.gender

  // Effects
  // ------------------------------------------------------------------------- /

  useEffect(() => {
    if (actorId) {
      mixpanel.disableIfTestUser(actorId.toString())
    }

    const actorAge = actorBirthDate ? differenceInYears(new Date(), new Date(actorBirthDate)) : undefined

    const actorAgeSegment = actorAge ? getAgeSegment(actorAge) : undefined
    mixpanel.registerProperties({
      age_segment: actorAgeSegment,
      gender: actorGender,
      using_mandate: loggedInForMandatee,
    })
  }, [actorId, actorBirthDate, actorGender, loggedInForMandatee])

  useEffect(() => {
    // Reactivate subject if user logs in
    if (!previousAuthState.current && isAuthenticated && subjectMember?.status === 'deactivated') {
      mutate()
        .then(
          async () =>
            await updateSubject({
              status: 'active',
            }),
        )
        .then(() => {
          mixpanel.track('account_reactivate')
        })
        .catch((error) => {
          console.error(error)
        })
    }

    previousAuthState.current = isAuthenticated
  }, [isAuthenticated, mutate, subjectMember?.status, subjectMember, updateSubject])

  useEffect(() => {
    // If logged in for a mandatee, ensure any persistent lockout state is cleared
    if (loggedInForMandatee) {
      setLockout(false)
    }
  }, [loggedInForMandatee, setLockout])

  // Render
  // ------------------------------------------------------------------------- /

  // 1. If auth is still bootstrapping (token not loaded yet), wait.
  // This prevents redirect loops like: home -> login -> home -> ...
  // Added lockout === null check to wait for lockout state as well.
  if (isAuthBootstrapping || isLocked === null || isLocked === undefined) {
    return <AnimatedLoader cancellable />
  }

  // 2. Only redirect if there is NO token or the user is locked out
  if (!isAuthenticated || isLocked) {
    return <Navigate to={`/${tenant}/login`} replace />
  }

  // 3. If the token exists but data (actorMember) is missing, wait (show loader)
  // This prevents the false positive redirect to login
  // Loader while we don't have enough info
  if (isLoading) {
    return <AnimatedLoader cancellable />
  }

  // 4. Proceed with other checks (Biometrics, Onboarding, etc.)
  if (!isOnboardingRoute && subjectMember?.status === 'active' && !subjectMember?.meta.onboarding?.completed) {
    return <Navigate to={`/${tenant}/onboarding`} replace />
  }

  if (
    !loggedInForMandatee &&
    subjectMember?.status === 'active' &&
    subjectMember?.meta.onboarding?.completed &&
    biometricsAreAvailable &&
    biometricPermission === BiometricsAppPermission.PROMPT &&
    !isBiometricRoute
  ) {
    return <Navigate to={`/${tenant}/biometric`} replace />
  }

  // All good → render private routes
  return (
    <>
      <NativePlatform>
        <PushNotificationsProvider />
      </NativePlatform>
      <IdleTimer>{children}</IdleTimer>
    </>
  )
}

export { Authenticated }
